import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';

const TABS = ['Show History', 'Shows by Year', 'Venues Visited', 'Artists Watched', 'Top Cities', 'Shows Map'];
const PAGE_SIZE = 25;

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    if (totalPages <= 1) return null;
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '20px', fontSize: '14px' }}>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={pgBtn}>←</button>
            <span style={{ color: '#aaa' }}>Page {currentPage} of {totalPages}</span>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={pgBtn}>→</button>
        </div>
    );
}
const pgBtn = { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', color: '#e0e0e0', padding: '5px 12px', cursor: 'pointer', fontSize: '14px' };

// ── Show History Tab ──────────────────────────────────────────────────────────
function ShowHistoryTab({ shows }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet. Start marking events as Attended!</div>;

    const q = search.toLowerCase();
    const filtered = search ? shows.filter(s =>
        [s.event_date ? new Date(s.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '',
         s.city, s.state, s.country, s.venue_name, s.artists, s.festival_name, s.role]
            .some(v => v && v.toLowerCase().includes(q))
    ) : shows;

    const totalShows = shows.length;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, city, venue, artists, role..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>#</th>
                                    <th style={styles.th}>Role</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>Artists</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const globalIdx = shows.indexOf(show);
                                    const showNum = totalShows - globalIdx;
                                    const dateObj = show.event_date ? new Date(show.event_date) : null;
                                    const formattedDate = dateObj
                                        ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';
                                    const isPerformed = show.role === 'Performed';
                                    const isWorked = show.role === 'Worked';
                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{showNum}</Link>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={isPerformed ? styles.rolePerformed : isWorked ? styles.roleWorked : styles.roleAttended}>
                                                    {show.role}
                                                </span>
                                            </td>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formattedDate}</td>
                                            <td style={styles.td}>{show.city || '—'}</td>
                                            <td style={styles.td}>{show.state || '—'}</td>
                                            <td style={styles.td}>{show.country || '—'}</td>
                                            <td style={styles.td}>
                                                {show.venue_id
                                                    ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link>
                                                    : show.venue_name || '—'}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.festival_name && show.festival_slug
                                                    ? <><Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{show.artists ? ` - ${show.artists}` : ''}</>
                                                    : show.artists || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Shows by Year Tab ─────────────────────────────────────────────────────────
function ShowsByYearTab({ shows }) {
    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet.</div>;

    const years = shows.filter(s => s.event_date).map(s => new Date(s.event_date).getUTCFullYear());
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const counts = {};
    years.forEach(y => { counts[y] = (counts[y] || 0) + 1; });
    const data = [];
    for (let y = minYear; y <= maxYear; y++) data.push({ year: y, count: counts[y] || 0 });

    const maxCount = Math.max(...data.map(d => d.count), 1);
    const barMaxHeight = 200;
    const step = maxCount <= 5 ? 1 : maxCount <= 10 ? 2 : maxCount <= 20 ? 5 : 10;
    const gridLines = [];
    for (let v = 0; v <= maxCount; v += step) gridLines.push(v);
    if (gridLines[gridLines.length - 1] < maxCount) gridLines.push(maxCount);

    const chartWidth = Math.max(data.length * 52, 400);
    const yAxisWidth = 36;
    const chartPadTop = 16;
    const chartPadBottom = 40;
    const totalHeight = barMaxHeight + chartPadTop + chartPadBottom;

    return (
        <div>
            <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                <div style={{ display: 'flex', minWidth: chartWidth + yAxisWidth + 'px' }}>
                    <div style={{ width: yAxisWidth + 'px', flexShrink: 0, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => {
                            const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight;
                            return <div key={v} style={{ position: 'absolute', top: top + 'px', right: '6px', transform: 'translateY(-50%)', fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>{v}</div>;
                        })}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => {
                            const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight;
                            return <div key={v} style={{ position: 'absolute', top: top + 'px', left: 0, right: 0, borderTop: `1px solid ${v === 0 ? '#444' : '#2a2a2a'}`, zIndex: 0 }} />;
                        })}
                        <div style={{ display: 'flex', alignItems: 'flex-end', height: barMaxHeight + 'px', marginTop: chartPadTop + 'px', gap: '4px', paddingLeft: '8px', paddingRight: '8px', position: 'relative', zIndex: 1 }}>
                            {data.map(({ year, count }) => {
                                const barH = count === 0 ? 0 : Math.max((count / maxCount) * barMaxHeight, 4);
                                return (
                                    <div key={year} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }} title={`${year}: ${count} show${count !== 1 ? 's' : ''}`}>
                                        {count > 0 && <div style={{ fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', opacity: 0.85 }}>{count}</div>}
                                        <div style={{ width: '100%', maxWidth: '44px', height: barH + 'px', backgroundColor: count === 0 ? 'transparent' : '#bb86fc', borderRadius: '3px 3px 0 0' }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', paddingLeft: '8px', paddingRight: '8px', gap: '4px', marginTop: '6px' }}>
                            {data.map(({ year, count }) => (
                                <div key={year} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: count === 0 ? '#444' : '#888', whiteSpace: 'nowrap', overflow: 'hidden' }}>{year}</div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '32px', fontSize: '13px', color: '#888' }}>
                <span>Total Years Active: <strong style={{ color: '#e0e0e0' }}>{data.filter(d => d.count > 0).length}</strong></span>
                <span>Peak Year: <strong style={{ color: '#bb86fc' }}>{data.reduce((best, d) => d.count > best.count ? d : best, data[0]).year} ({data.reduce((best, d) => d.count > best.count ? d : best, data[0]).count} shows)</strong></span>
            </div>
        </div>
    );
}

// ── Venues Visited Tab ────────────────────────────────────────────────────────
function VenuesVisitedTab({ venues }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!venues || venues.length === 0) return <div style={styles.emptyTab}>No venues recorded yet.</div>;

    const q = search.toLowerCase();
    const filtered = search
        ? venues.filter(v => [v.name, v.city, v.state, v.country].some(val => val && val.toLowerCase().includes(q)))
        : venues;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input type="text" placeholder="Search by venue, city, state, country..." value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No venues match your search.</div> : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State/Region</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Times Visited</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((v, idx) => (
                                    <tr key={v.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}><Link to={`/venues/${v.slug || v.id}`} style={styles.tableLink}>{v.name}</Link></td>
                                        <td style={styles.td}>{v.city || '—'}</td>
                                        <td style={styles.td}>{v.state || '—'}</td>
                                        <td style={styles.td}>{v.country || '—'}</td>
                                        <td style={styles.td}><span style={styles.showCount}>{v.times_played}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Artists Watched Tab ───────────────────────────────────────────────────────
function ArtistsWatchedTab({ artists }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!artists || artists.length === 0) return <div style={styles.emptyTab}>No artists marked as "Caught This" yet.</div>;

    const q = search.toLowerCase();
    const filtered = search
        ? artists.filter(a => [a.name, a.city, a.state, a.country].some(val => val && val.toLowerCase().includes(q)))
        : artists;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input type="text" placeholder="Search by artist, city, state, country..." value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No artists match your search.</div> : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Artist</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State/Region</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Times Watched</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((a, idx) => (
                                    <tr key={a.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}><Link to={`/artists/${a.slug || a.id}`} style={styles.tableLink}>{a.name}</Link></td>
                                        <td style={styles.td}>{a.city || '—'}</td>
                                        <td style={styles.td}>{a.state || '—'}</td>
                                        <td style={styles.td}>{a.country || '—'}</td>
                                        <td style={styles.td}><span style={styles.showCount}>{a.times_watched}x</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Top Cities Tab ────────────────────────────────────────────────────────────
function TopCitiesTab({ geography }) {
    if (!geography || geography.length === 0) return <div style={styles.emptyTab}>No geographic data available.</div>;

    // Hierarchical rollup: country > state > city > venue
    const rollup = {};
    geography.forEach(row => {
        const country = row.country || 'Unknown';
        const state = row.state || '—';
        const city = row.city || '—';
        const count = parseInt(row.show_count) || 0;
        if (!rollup[country]) rollup[country] = { total: 0, states: {} };
        rollup[country].total += count;
        if (!rollup[country].states[state]) rollup[country].states[state] = { total: 0, cities: {} };
        rollup[country].states[state].total += count;
        if (!rollup[country].states[state].cities[city]) rollup[country].states[state].cities[city] = { total: 0, venues: {} };
        rollup[country].states[state].cities[city].total += count;
        const venueKey = `${row.venue_id}__${row.venue_name}`;
        if (!rollup[country].states[state].cities[city].venues[venueKey]) {
            rollup[country].states[state].cities[city].venues[venueKey] = { id: row.venue_id, name: row.venue_name, slug: row.venue_slug, count: 0 };
        }
        rollup[country].states[state].cities[city].venues[venueKey].count += count;
    });

    const allRows = [];
    Object.entries(rollup).sort((a, b) => b[1].total - a[1].total).forEach(([country, cData]) => {
        let countryFirst = true;
        Object.entries(cData.states).sort((a, b) => b[1].total - a[1].total).forEach(([state, sData]) => {
            let stateFirst = true;
            Object.entries(sData.cities).sort((a, b) => b[1].total - a[1].total).forEach(([city, cityData]) => {
                let cityFirst = true;
                Object.values(cityData.venues).sort((a, b) => b.count - a.count).forEach(venue => {
                    allRows.push({ countryFirst, stateFirst, cityFirst, country, state, city, cTotal: cData.total, sTotal: sData.total, cityTotal: cityData.total, venue });
                    countryFirst = false; stateFirst = false; cityFirst = false;
                });
            });
        });
    });

    return (
        <div style={styles.tableContainer}>
            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.th}>Country</th>
                        <th style={styles.th}>State/Region</th>
                        <th style={styles.th}>City</th>
                        <th style={styles.th}>Venue</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Shows</th>
                    </tr>
                </thead>
                <tbody>
                    {allRows.map((row, idx) => (
                        <tr key={idx} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                            <td style={{ ...styles.td, fontWeight: row.countryFirst ? 'bold' : 'normal', color: row.countryFirst ? '#e0e0e0' : 'transparent' }}>
                                {row.countryFirst ? <span>{row.country} <span style={styles.geoSubCount}>({row.cTotal})</span></span> : '\u00a0'}
                            </td>
                            <td style={{ ...styles.td, paddingLeft: '24px', color: row.stateFirst ? '#ccc' : 'transparent' }}>
                                {row.stateFirst ? <span>{row.state} <span style={styles.geoSubCount}>({row.sTotal})</span></span> : '\u00a0'}
                            </td>
                            <td style={{ ...styles.td, paddingLeft: '24px', color: row.cityFirst ? '#bbb' : 'transparent' }}>
                                {row.cityFirst ? <span>{row.city} <span style={styles.geoSubCount}>({row.cityTotal})</span></span> : '\u00a0'}
                            </td>
                            <td style={{ ...styles.td, paddingLeft: '32px' }}>
                                {row.venue.id
                                    ? <Link to={`/venues/${row.venue.slug || row.venue.id}`} style={styles.tableLink}>{row.venue.name}</Link>
                                    : <span style={{ color: '#aaa' }}>{row.venue.name}</span>}
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right' }}>
                                <span style={styles.showCount}>{row.venue.count}</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── Shows Map Tab ─────────────────────────────────────────────────────────────
function ShowsMapTab({ showMap }) {
    const mapRef = React.useRef(null);
    const mapInstanceRef = React.useRef(null);
    const [leafletReady, setLeafletReady] = React.useState(!!window.L);

    React.useEffect(() => {
        if (window.L) { setLeafletReady(true); return; }
        if (!document.getElementById('leaflet-css')) {
            const link = document.createElement('link');
            link.id = 'leaflet-css'; link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
        }
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => setLeafletReady(true);
        document.head.appendChild(script);
    }, []);

    React.useEffect(() => {
        if (!leafletReady || !mapRef.current || !showMap || showMap.length === 0) return;
        if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
        const L = window.L;
        const map = L.map(mapRef.current, { zoomControl: true });
        mapInstanceRef.current = map;
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors', maxZoom: 18,
        }).addTo(map);
        const markerIcon = L.divIcon({
            className: '',
            html: `<div style="width:12px;height:12px;background:#bb86fc;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.5);"></div>`,
            iconSize: [12, 12], iconAnchor: [6, 6], popupAnchor: [0, -8],
        });
        const bounds = [];
        showMap.forEach(venue => {
            const lat = parseFloat(venue.lat);
            const lng = parseFloat(venue.lng);
            if (isNaN(lat) || isNaN(lng)) return;
            bounds.push([lat, lng]);
            const dates = venue.show_dates
                ? venue.show_dates.split(',').map(d => new Date(d.trim()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }))
                : [];
            const locationStr = [venue.city, venue.state, venue.country].filter(Boolean).join(', ');
            const showCount = parseInt(venue.show_count);
            const popupHtml = `
                <div style="font-family:sans-serif;min-width:180px;max-width:260px;">
                    <div style="font-weight:bold;font-size:14px;margin-bottom:4px;">
                        <a href="/venues/${venue.venue_slug || venue.venue_id}" style="color:#bb86fc;text-decoration:none;">${venue.venue_name} ↗</a>
                    </div>
                    ${locationStr ? `<div style="font-size:12px;color:#888;margin-bottom:6px;">${locationStr}</div>` : ''}
                    <div style="font-size:12px;color:#aaa;margin-bottom:${dates.length ? '6px' : '0'};">
                        <strong style="color:#e0e0e0;">${showCount}</strong> show${showCount !== 1 ? 's' : ''}
                    </div>
                    ${dates.length > 0 ? `<div style="font-size:11px;color:#999;line-height:1.6;">${dates.slice(0, 10).join('<br/>')}${dates.length > 10 ? `<br/><em>+${dates.length - 10} more</em>` : ''}</div>` : ''}
                </div>`;
            L.marker([lat, lng], { icon: markerIcon }).bindPopup(popupHtml, { maxWidth: 280 }).addTo(map);
        });
        if (bounds.length === 1) map.setView(bounds[0], 12);
        else if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40] });
        return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
    }, [leafletReady, showMap]);

    if (!showMap || showMap.length === 0) return <div style={styles.emptyTab}>No geocoded venues available yet.</div>;

    return (
        <div>
            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#888' }}>
                {showMap.length} venue{showMap.length !== 1 ? 's' : ''} with location data · Click a pin for details
            </div>
            {!leafletReady
                ? <div style={styles.emptyTab}>Loading map...</div>
                : <div ref={mapRef} style={{ height: '480px', borderRadius: '8px', border: '1px solid #333', overflow: 'hidden' }} />
            }
        </div>
    );
}

// ── MyShows ───────────────────────────────────────────────────────────────────
function MyShows() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        document.title = 'SHARC - My Shows';
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    useEffect(() => {
        async function load() {
            try {
                const token = await getIdToken();
                const res = await axios.get(`${config.API_URL}/user/shows`, {
                    headers: { Authorization: token }
                });
                setData(res.data);
                setLoading(false);
            } catch (e) {
                setError('Failed to load your shows.');
                setLoading(false);
            }
        }
        load();
    }, []);

    if (loading) return <div style={styles.message}>Loading your shows...</div>;
    if (error) return <div style={styles.errorMsg}>{error}</div>;
    if (!data) return null;

    const { user, stats, show_history, venues_visited, artists_watched, geography, show_map } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    };

    const locationStr = [user.display_name && user.city ? user.city : null, user.state, user.country]
        .filter(Boolean).join(', ');

    return (
        <div>
            {/* Nav */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Link to="/" style={styles.backLink}>← Home</Link>
                <Link to="/profile" style={styles.editProfileLink}>Edit Profile</Link>
            </div>

            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerInitials}>
                    {user.display_name
                        ? user.display_name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
                        : '?'}
                </div>
                <div style={styles.headerText}>
                    <h1 style={styles.title}>{user.display_name || 'My Shows'}</h1>
                    {locationStr && <div style={styles.locationStr}>{locationStr}</div>}
                    {user.member_name && (
                        <div style={styles.memberBadge}>
                            Member of <Link to={`/members/${user.member_slug}`} style={styles.memberLink}>{user.member_name}</Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.total_shows}</div>
                    <div style={styles.statLabel}>Total Shows</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.attended_shows}</div>
                    <div style={styles.statLabel}>Attended</div>
                </div>
                {stats.performed_shows > 0 && (
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{stats.performed_shows}</div>
                        <div style={styles.statLabel}>Performed</div>
                    </div>
                )}
                {stats.worked_shows > 0 && (
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{stats.worked_shows}</div>
                        <div style={styles.statLabel}>Worked</div>
                    </div>
                )}
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{venues_visited.length}</div>
                    <div style={styles.statLabel}>Venues Visited</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.first_show_date ? formatDate(stats.first_show_date) : '—'}</div>
                    <div style={styles.statLabel}>First Show</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.last_show_date ? formatDate(stats.last_show_date) : '—'}</div>
                    <div style={styles.statLabel}>Most Recent Show</div>
                </div>
            </div>

            {/* Tabs */}
            <div style={styles.tabsWrapper}>
                <div style={styles.tabBar}>
                    {TABS.map((tab, i) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(i)}
                            style={{ ...styles.tabBtn, ...(activeTab === i ? styles.tabBtnActive : {}) }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div style={styles.tabContent}>
                    {activeTab === 0 && <ShowHistoryTab shows={show_history} />}
                    {activeTab === 1 && <ShowsByYearTab shows={show_history} />}
                    {activeTab === 2 && <VenuesVisitedTab venues={venues_visited} />}
                    {activeTab === 3 && <ArtistsWatchedTab artists={artists_watched} />}
                    {activeTab === 4 && <TopCitiesTab geography={geography} />}
                    {activeTab === 5 && <ShowsMapTab showMap={show_map} />}
                </div>
            </div>
        </div>
    );
}

const styles = {
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    editProfileLink: { color: '#aaa', textDecoration: 'none', fontSize: '13px', border: '1px solid #444', borderRadius: '4px', padding: '5px 12px' },
    header: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '32px' },
    headerInitials: { width: '64px', height: '64px', borderRadius: '10px', backgroundColor: '#bb86fc', color: '#121212', fontSize: '24px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, letterSpacing: '1px' },
    headerText: { flex: 1 },
    title: { fontSize: '36px', fontWeight: 'bold', color: '#e0e0e0', margin: '0 0 4px 0' },
    locationStr: { fontSize: '15px', color: '#aaa', marginBottom: '4px' },
    memberBadge: { fontSize: '13px', color: '#888' },
    memberLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '40px' },
    statCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px', textAlign: 'center' },
    statValue: { fontSize: '22px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '12px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    tabsWrapper: { marginTop: '8px' },
    tabBar: { display: 'flex', flexWrap: 'wrap', borderBottom: '2px solid #2a2a2a', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', color: '#aaa', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
    tabBtnActive: { color: '#bb86fc', borderBottomColor: '#bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '100px' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '10px 16px', color: '#e0e0e0', verticalAlign: 'middle' },
    trEven: { backgroundColor: 'transparent' },
    trOdd: { backgroundColor: '#1e1e1e' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    roleAttended: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    rolePerformed: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    roleWorked: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    geoSubCount: { fontSize: '11px', color: '#666', fontWeight: 'normal' },
    emptyTab: { color: '#666', fontSize: '14px', fontStyle: 'italic', padding: '24px 0' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    errorMsg: { textAlign: 'center', color: '#e57373', fontSize: '16px', padding: '60px 0' },
};

export default MyShows;
